/**
 * domain-kjop.ts — innhold for /domener/kjop («Kjøp et domene»), norsk + engelsk
 *
 * ⚠ PLASSHOLDERE: funksjoner (DNSSEC, WHOIS-personvern, lås, e-postvideresending),
 * registreringsperiode og FAQ må bekreftes før publisering.
 */

import type { Locale } from '@/types/landing';
import type { DomainBuyPageContent } from '@/types/domain';
import { DOMAIN_CONTACT } from './domain-shared';

const no: DomainBuyPageContent = {
  seoTitle: 'Kjøp et domene — inDomain',
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }, { label: 'Domener', href: '/domener/sok' }],
    current: 'Kjøp et domene',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'endelser', label: 'Populære endelser' },
      { id: 'steg', label: 'Registrer i tre steg' },
      { id: 'funksjoner', label: 'Dette får du' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se alle endelser', href: '/domener/endelser' },
  },
  hero: {
    heading: 'Kjøp et domene',
    subheading: 'Registrer et domenenavn for nettstedet ditt.',
    primaryCta: { label: 'Se populære endelser', href: '#endelser' },
    secondaryCta: { label: 'Flytt et domene', href: '/domener/flytt' },
    illustrationLabel: 'Illustrasjon av et adressefelt, en klode og en prislapp',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Navnet ditt. På nett.',
    body: [
      'Med inDomain registrerer og administrerer du domenenavn på samme sted som resten av tjenestene dine. Du velger endelsen, og vi hjelper deg med oppsett.',
      'Alle domener følger med DNS-administrasjon, og du kan skjule personopplysningene dine i WHOIS.',
    ],
  },
  steps: {
    eyebrow: 'Slik gjør du det',
    heading: 'Registrer i tre steg',
    intro: 'Tre steg fra idé til registrert domene.',
    panelLabel: 'Illustrasjon av et adressefelt og en klode',
    items: [
      { title: 'Søk etter navnet', body: 'Skriv inn navnet du ønsker, og se priser for ulike endelser.' },
      { title: 'Velg endelse og periode', body: 'Velg endelsen som passer, og hvor lenge du vil registrere domenet.' },
      { title: 'Vi registrerer domenet', body: 'Du ber om registrering, så sjekker vi at domenet er ledig og registrerer det for deg.' },
    ],
  },
  popular: {
    eyebrow: 'Populære endelser',
    heading: 'Populære domeneendelser',
    intro: 'Velg endelsen som passer navnet og virksomheten din.',
    priceFrom: 'Fra',
    perYear: 'per år',
    footnote: '*Prisene er veiledende, per år og eksklusiv mva.',
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Det du trenger for å eie og styre domenet.',
    panelLabel: 'Illustrasjon av et adressefelt og en klode',
    items: [
      { title: 'DNS-administrasjon', body: 'Styr DNS-oppføringene dine, og pek domenet mot nettsted, e-post eller server.' },
      { title: 'Personvern i WHOIS', body: 'Skjul personopplysningene dine i den offentlige WHOIS-oppføringen.' },
      { title: 'Automatisk fornyelse', body: 'Domenet fornyes automatisk, så du ikke mister det ved en glipp.' },
      { title: 'E-postvideresending', body: 'Send e-post til navn@dittdomene videre til en eksisterende adresse.' },
      { title: 'Lås mot uønsket flytting', body: 'Domenet er låst mot flytting uten at du har godkjent det.' },
      { title: 'DNSSEC', body: 'Signerte DNS-oppføringer som beskytter mot forfalskede svar.' },
    ],
  },
  promo: { title: 'Har du allerede et domene?', body: 'Flytt det til inSuite og samle alt på ett sted.', cta: { label: 'Flytt domenet', href: '/domener/flytt' } },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      { question: 'Hvordan registrerer jeg et domene?', answer: 'Søk etter navnet, og trykk «Be om registrering». Vi sjekker at domenet er ledig og registrerer det for deg.' },
      { question: 'Hvor lenge varer registreringen?', answer: 'Domener registreres for ett år av gangen, og fornyes automatisk hvis du ikke sier opp.' },
      { question: 'Hvem kan registrere et .no-domene?', answer: 'For .no stiller registeret krav til tilknytning til Norge. Ta kontakt for detaljer om hva som gjelder for deg.' },
      { question: 'Hva er personvern i WHOIS?', answer: 'Det skjuler personopplysningene dine i den offentlige oppføringen for domenet, så de ikke er synlige for alle.' },
      { question: 'Kan jeg bruke domenet med tjenestene deres?', answer: 'Ja. Du peker domenet mot inHost, inWordPress, inNode eller inVPS, og vi hjelper deg med oppsettet.' },
    ],
  },
  cta: { heading: 'Klar til å registrere?', body: 'Kontakt oss, så registrerer vi domenet for deg.', primary: { label: 'Kontakt oss', href: DOMAIN_CONTACT } },
};

const en: DomainBuyPageContent = {
  seoTitle: 'Buy a domain — inDomain',
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }, { label: 'Domains', href: '/domener/sok' }],
    current: 'Buy a domain',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'endelser', label: 'Popular extensions' },
      { id: 'steg', label: 'Register in three steps' },
      { id: 'funksjoner', label: 'What you get' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See all extensions', href: '/domener/endelser' },
  },
  hero: {
    heading: 'Buy a domain',
    subheading: 'Register a domain name for your website.',
    primaryCta: { label: 'See popular extensions', href: '#endelser' },
    secondaryCta: { label: 'Transfer a domain', href: '/domener/flytt' },
    illustrationLabel: 'Illustration of an address bar, a globe and a price tag',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'Your name. Online.',
    body: [
      'With inDomain you register and manage domain names in the same place as the rest of your services. You pick the extension, and we help you set it up.',
      'Every domain comes with DNS management, and you can hide your personal details in WHOIS.',
    ],
  },
  steps: {
    eyebrow: 'How to do it',
    heading: 'Register in three steps',
    intro: 'Three steps from idea to registered domain.',
    panelLabel: 'Illustration of an address bar and a globe',
    items: [
      { title: 'Search for the name', body: 'Enter the name you want, and see prices for different extensions.' },
      { title: 'Pick an extension and period', body: 'Choose the extension that fits, and how long you want to register the domain.' },
      { title: 'We register the domain', body: 'You request registration, and we check that the domain is available and register it for you.' },
    ],
  },
  popular: {
    eyebrow: 'Popular extensions',
    heading: 'Popular domain extensions',
    intro: 'Pick the extension that fits your name and your business.',
    priceFrom: 'From',
    perYear: 'per year',
    footnote: '*Prices are indicative, per year and exclude VAT.',
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'What you need to own and manage your domain.',
    panelLabel: 'Illustration of an address bar and a globe',
    items: [
      { title: 'DNS management', body: 'Manage your DNS records, and point the domain to a website, email or a server.' },
      { title: 'WHOIS privacy', body: 'Hide your personal details in the public WHOIS record.' },
      { title: 'Automatic renewal', body: 'The domain renews automatically, so you do not lose it by mistake.' },
      { title: 'Email forwarding', body: 'Forward email sent to name@yourdomain to an existing address.' },
      { title: 'Transfer lock', body: 'The domain is locked against transfers you have not approved.' },
      { title: 'DNSSEC', body: 'Signed DNS records that protect against forged answers.' },
    ],
  },
  promo: { title: 'Already have a domain?', body: 'Move it to inSuite and keep everything in one place.', cta: { label: 'Transfer the domain', href: '/domener/flytt' } },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      { question: 'How do I register a domain?', answer: 'Search for the name, and press “Request registration”. We check that the domain is available and register it for you.' },
      { question: 'How long does a registration last?', answer: 'Domains are registered one year at a time, and renew automatically unless you cancel.' },
      { question: 'Who can register a .no domain?', answer: 'For .no the registry sets requirements for a connection to Norway. Get in touch for details on what applies to you.' },
      { question: 'What is WHOIS privacy?', answer: 'It hides your personal details in the public record for the domain, so they are not visible to everyone.' },
      { question: 'Can I use the domain with your services?', answer: 'Yes. You point the domain to inHost, inWordPress, inNode or inVPS, and we help you with the setup.' },
    ],
  },
  cta: { heading: 'Ready to register?', body: 'Contact us, and we will register the domain for you.', primary: { label: 'Contact us', href: DOMAIN_CONTACT } },
};

export const DOMAIN_KJOP: Record<Locale, DomainBuyPageContent> = { no, en };
